<template>
	<form action="/search" method="get" role="search">
		<div class="inner">
			<div class="search-form">
				<label for="SlideMiniSearch" class="visually-hidden">
					{{ $language.search.placeholder }}
				</label>

				<input
					v-model="query"
					@keyup="search($event)"
					ref="input"
					:placeholder="$language.search.placeholder"
					type="search"
					name="q"
					id="SlideMiniSearch"
					autocomplete="off">

				<button type="submit" class="btn">
					<IconSearchMobile />
					<IconSearch />
					<span class="icon-fallback-text">{{ $language.search.submit }}</span>
				</button>
			</div><!-- search-form -->

			<div class="instant-search-results" v-if="all_empty == false">
				<section class="results-group" v-if="suggestions.length">
					<h6>{{ $language.search.suggestions }}</h6>
					<ul>
						<li
							v-for="(suggestion, idx) in suggestions"
							:key="idx"
							@click="searchSuggestion(suggestion)"
							class="no-link">
							{{ suggestion }}
						</li>
					</ul>
				</section>

				<section class="results-group" v-if="collections.length">
					<h6>{{ $language.search.collections }}</h6>
					<ul>
						<li v-for="(collection, idx) in collections" :key="idx">
							<a :href="`/collections/${ collection.handle }`">
								{{ collection.title }}
							</a>
						</li>
					</ul>
				</section>

				<section class="results-group" v-if="pages.length">
					<h6>{{ $language.search.pages }}</h6>
					<ul>
						<li v-for="(page, idx) in pages" :key="idx">
							<a :href="page.url">
								{{ page.title }}
							</a>
						</li>
					</ul>
				</section>

				<section class="results-group results-group-products" v-if="products.length">
					<h6 class="visually-hidden">{{ $language.search.products }}</h6>
					<ul>
						<li v-for="(product, idx) in products" :key="idx">
							<a :href="`/products/${ product.handle }`">
								<div class="product-image">
									<img
										:src="image(product)"
										:srcset="imageSrcset(product)"
										sizes="(min-width: 1640px) 150px, 100px"
										:alt="title(product)"/>
								</div><!-- product-image -->

								<div class="product-text">
									<p class="title">{{ title(product) }}</p>
									<p class="colour">{{ colour(product)}}</p>
									<p class="vendor" v-if="product.vendor">{{ product.vendor }}</p>
									<p class="price">{{ moneyFormat(product, 'price_min') }}</p>
								</div><!-- product-text -->
							</a>
						</li>
					</ul>
				</section>
			</div><!-- instant-search-results -->

			<div :class="['bottom-links', { 'both-shown': all_empty == true && quick_links.length }]">
				<div class="quick-links" v-if="all_empty == true && quick_links.length">
					<p>{{ $language.search.quick_links }}</p>
					<ul>
						<li v-for="link in quick_links">
							<a :href="link.url">
								{{ link.title }}
							</a>
						</li>
					</ul>
				</div><!-- quick-links -->

				<div :class="['action-links', { 'products-found': all_empty == false }]">
					<a href="#" class="close-search link-underline toggle-search">{{ $language.close }}</a>

					<button type="submit" class="btn results-all-products link-underline" v-if="all_empty == false" v-show="products_found">
						{{ $language.search.view_all }}
					</button>
				</div><!-- action-links -->
			</div><!-- bottom-links -->
		</div><!-- inner -->
	</form>
</template>

<script>
import debounce from 'lodash/debounce'
import { formatMoney } from '@shopify/theme-currency'
import { getSizedImageUrl } from '@shopify/theme-images'
import IconSearch from '../Icons/IconSearch'
import IconSearchMobile from '../Icons/IconSearchMobile'

export default {
	components: { IconSearch, IconSearchMobile },

	props: ['quick_links'],

	data () {
		return {
			query: '',
			suggestions: [],
			collections: [],
			pages: [],
			products: [],
			products_found: 0,
			all_empty: true
		}
	},

	methods: {
		search: debounce(function (e) {
			this.products = []

			console.log('here?')

			// Escape
			if (e.key === 'Escape' || e.keyCode === 27) {
				this.query = ''
				this.all_empty = true
				this.$refs.input.blur()
				return
			}

			// Reset
			if (this.query.length < 2) {
				this.all_empty = true
				return
			}

			// BCSF Endpoint
			let url = new URL('https://services.mybcapps.com/bc-sf-filter/search/suggest')

			// Request options
			let params = {
				shop: theme.domain,
				q: this.query,
				suggest_types: ['products', 'pages', 'collections'],
				product_limit: 3
			}

			// Create query string from params object
			Object.keys(params).forEach(key => {
				url.searchParams.append(key, params[key])
			})

			// Send request
			fetch(url)
				.then(response => response.json())
				.then(data => {
					this.all_empty = data.all_empty;
					this.suggestions = data.suggestions;
					this.collections = data.collections.filter(c => !c.handle.includes('sale'));
					this.pages = data.pages;
					this.products = data.products;
					this.products_found = data.total_product;
				})
				.catch(error => {
					console.log('error', error)
				})
		}, 100),

		searchSuggestion (suggestion) {
			this.query = suggestion
			this.$refs.input.value = suggestion
			this.$parent.$el.querySelector('form[action="/search"]').submit()
		},

		moneyFormat (product, type) {
			return formatMoney(product[type] * 100, theme.moneyFormat)
		},

		title (product) {
			return product.title.split(' / ')[0]
		},

		colour (product) {
			return product.title.split(' / ')[1]
		},

		image (product) {
			let imageSrc = product.images['1']

			if(product.images['2']) {
				imageSrc = product.images['2']
			}

			return this.imageFormat(imageSrc, '150x225_crop_center')
		},

		imageSrcset (product) {
			let imageSrc = product.images['1']

			if(product.images['2']) {
				imageSrc = product.images['2']
			}

			return this.srcsetFormat(imageSrc, ['150x225_crop_center', '200x300_crop_center', '300x450_crop_center'])
		},

		imageFormat (src, size) {
			if(!src) {
				src = theme.defaultImage
			}

			return getSizedImageUrl(src, size)
		},

		srcsetFormat (src, sizes) {
			let output = ''

			if(!src) {
				src = theme.defaultImage
			}

			sizes.forEach(function (size, i) {
				if(i !== 0) {
					output += ', '
				}

				// Get first number from Shopify image size (split at x)
				let srcsetWidth = size.split('x', 1)[0]

				output += getSizedImageUrl(src, size) + ' ' + srcsetWidth + 'w'
			})

			return output
		}
	}
}
</script>
